import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CubeIcon,
  MapPinIcon,
  CalendarIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  UserIcon,
  CheckCircleIcon,
  PhoneIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { deviseCommande } from '../utils/devise';

/**
 * Page publique affichant les commandes du jour d'un utilisateur
 * Sans authentification - accès via URL avec token UUID unique
 * Inclut un bouton WhatsApp (wa.me) avec message MSGLIVREUR pré-rempli pour chaque commande
 */
const DailyOrdersPublicView = () => {
  // Récupérer le token d'accès depuis l'URL
  const { token } = useParams();

  // États pour les données et le chargement
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État pour afficher/masquer les commandes livrées - Masqué par défaut
  const [showDelivered, setShowDelivered] = useState(false);

  // État pour le filtre de date (défaut = aujourd'hui)
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // État pour le modal de détails
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // État pour le modal de saisie du numéro livreur (quand pas de livreur assigné)
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneModalOrder, setPhoneModalOrder] = useState(null);
  const [manualPhone, setManualPhone] = useState('');

  // État pour le lightbox d'image
  const [lightboxImage, setLightboxImage] = useState(null);

  /**
   * Récupère les commandes du jour via le token public
   */
  const fetchOrders = async (accessToken, date) => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      // Appel API publique sans authentification
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/daily-orders/public/${accessToken}`,
        { params: { date } }
      );
      setData(response.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des commandes:', err);
      if (err.response?.status === 404) {
        setError('Lien invalide ou expiré');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Impossible de charger les commandes. Vérifiez votre connexion.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charger les données au montage ou quand la date change
   */
  useEffect(() => {
    if (token) {
      fetchOrders(token, selectedDate);
    }
  }, [token, selectedDate]);

  /**
   * Ouvrir le modal de détails d'une commande
   */
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  /**
   * Fermer le modal de détails
   */
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedOrder(null);
  };

  /**
   * Prépare le message MSGLIVREUR en remplaçant les paramètres du template
   * par les données de la commande (même logique que scheduler.service.js prepareMessage)
   * @param {Object} order - La commande
   * @param {string} overrideDeliveryPhone - Numéro du livreur saisi manuellement (optionnel)
   * @returns {string} Le message préparé
   */
  const prepareWhatsAppMessage = (order, overrideDeliveryPhone = null) => {
    if (!data?.msglivreur_template?.message_template) return '';

    let message = data.msglivreur_template.message_template;

    // Mapping des noms de paramètres du template vers les données de la commande
    // Les paramètres du template MSGLIVREUR : order_id, delivery_number, price, phone, address, delivery_date
    const paramMapping = {
      order_id: order.order_number || '',
      order_number: order.order_number || '',
      // delivery_number = numéro du livreur assigné OU numéro saisi manuellement
      delivery_number: overrideDeliveryPhone || order.delivery_person?.phone || '',
      // price = montant total de la commande
      price: Number(order.total_amount).toLocaleString('fr-FR'),
      total_amount: Number(order.total_amount).toLocaleString('fr-FR'),
      amount: Number(order.total_amount).toLocaleString('fr-FR'),
      // phone = numéro du client
      phone: order.customer?.phone || '',
      // address = adresse de livraison
      address: order.delivery_address || '',
      delivery_address: order.delivery_address || '',
      // delivery_date = date de livraison formatée
      delivery_date: order.delivery_date
        ? new Date(order.delivery_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
        : '',
      delivery_day: order.delivery_date
        ? new Date(order.delivery_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
        : '',
      customer_name: order.customer?.name || '',
      name: order.customer?.name || '',
      status: getStatusLabel(order.status),
      notes: order.notes || '',
    };

    // Remplacer chaque paramètre du template par sa valeur correspondante
    if (data.msglivreur_template.parameters && Array.isArray(data.msglivreur_template.parameters)) {
      data.msglivreur_template.parameters.forEach(param => {
        const paramName = param.name;
        const paramValue = paramMapping[paramName] !== undefined ? paramMapping[paramName] : (order[paramName] || '');
        message = message.replace(new RegExp(`\\{${paramName}\\}`, 'g'), paramValue);
      });
    }

    return message;
  };

  /**
   * Nettoie un numéro de téléphone et le formate au format international
   * @param {string} phone - Numéro brut
   * @returns {string} Numéro nettoyé
   */
  const cleanPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/[\s\-+()]/g, '');
    // Si commence par 0, ajouter l'indicatif 225 (Côte d'Ivoire)
    if (cleaned.startsWith('0')) {
      cleaned = '225' + cleaned.substring(1);
    }
    // Si pas d'indicatif (numéro court), ajouter 225
    if (cleaned.length <= 10) {
      cleaned = '225' + cleaned;
    }
    return cleaned;
  };

  /**
   * Construit le lien wa.me avec le message pré-rempli
   * @param {Object} order - La commande
   * @param {string} overrideDeliveryPhone - Numéro du livreur saisi manuellement (optionnel)
   * @returns {string} L'URL wa.me complète
   */
  const buildWhatsAppLink = (order, overrideDeliveryPhone = null) => {
    const phone = order.customer?.phone;
    if (!phone) return null;
    const cleanPhone = cleanPhoneNumber(phone);
    const message = prepareWhatsAppMessage(order, overrideDeliveryPhone);
    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
  };

  /**
   * Gère le clic sur le bouton WhatsApp
   * Si pas de livreur assigné, ouvre un modal pour saisir le numéro
   * Sinon, ouvre directement wa.me
   */
  const handleWhatsAppClick = (e, order) => {
    e.stopPropagation();
    // Si pas de livreur assigné, ouvrir le modal de saisie du numéro
    if (!order.delivery_person?.phone) {
      setPhoneModalOrder(order);
      setManualPhone('');
      setShowPhoneModal(true);
      return;
    }
    // Livreur assigné : ouvrir directement wa.me
    const link = buildWhatsAppLink(order);
    if (link) window.open(link, '_blank');
  };

  /**
   * Valide le numéro saisi dans le modal et ouvre wa.me avec ce numéro en tant que delivery_number
   */
  const handlePhoneModalConfirm = () => {
    if (!manualPhone.trim() || !phoneModalOrder) return;
    const link = buildWhatsAppLink(phoneModalOrder, manualPhone.trim());
    if (link) window.open(link, '_blank');
    // Fermer le modal
    setShowPhoneModal(false);
    setPhoneModalOrder(null);
    setManualPhone('');
  };

  /**
   * Couleur du badge de statut
   */
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      preparing: 'bg-yellow-100 text-yellow-800',
      ready_to_deliver: 'bg-purple-100 text-purple-800',
      in_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      completed: 'bg-green-200 text-green-900',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  /**
   * Libellé du statut en français
   */
  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouvelle',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready_to_deliver: 'Prête',
      in_delivery: 'En livraison',
      delivered: 'Livrée',
      completed: 'Livrée',
      cancelled: 'Annulée',
    };
    return labels[status] || status;
  };

  /**
   * Tronque le texte à un nombre de mots maximum
   */
  const truncateWords = (text, maxWords) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  /**
   * Vérifie si une commande est livrée (statut completed)
   */
  const isOrderDelivered = (order) => {
    return order.status === 'completed';
  };

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-green-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Affichage si pas de token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <ClipboardDocumentListIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Lien requis</h2>
          <p className="text-gray-600">
            Veuillez accéder à cette page via le lien qui vous a été fourni.
          </p>
        </div>
      </div>
    );
  }

  // Filtrer les commandes livrées si toggle désactivé
  const filteredOrders = data?.orders?.filter(order => {
    if (!showDelivered && order.status === 'completed') return false;
    return true;
  }) || [];

  // Statistiques
  const totalOrders = data?.orders?.length || 0;
  const activeOrders = data?.orders?.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length || 0;
  const deliveredOrders = data?.orders?.filter(o => o.status === 'completed').length || 0;
  const totalAmount = data?.orders?.reduce((sum, o) => sum + Number(o.total_amount || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* En-tête avec nom de la boutique et statistiques */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Info boutique */}
            <div className="flex flex-col sm:flex-row items-center text-center sm:text-left gap-4">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ClipboardDocumentListIcon className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {data?.user_name || 'Boutique'}
                </h1>
                <p className="text-gray-600">Commandes du jour</p>
              </div>
            </div>

            {/* Filtres et stats */}
            <div className="flex flex-col sm:items-end gap-4 w-full sm:w-auto">
              <div className="flex flex-wrap items-center gap-3 w-full justify-center sm:justify-end">
                {/* Sélecteur de date */}
                <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                  <CalendarIcon className="h-4 w-4 text-gray-500" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-transparent text-xs font-bold focus:outline-none"
                  />
                </div>

                {/* Toggle livrées */}
                <button
                  onClick={() => setShowDelivered(!showDelivered)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-bold text-xs transition shadow-sm ${
                    showDelivered
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition ${
                    showDelivered ? 'bg-green-600 border-green-600' : 'bg-white border-gray-400'
                  }`}>
                    {showDelivered && <CheckCircleIcon className="h-3 w-3 text-white" />}
                  </div>
                  {showDelivered ? 'Livrées affichées' : 'Livrées masquées'}
                </button>
              </div>

              {/* Statistiques */}
              <div className="flex gap-3 w-full sm:w-auto">
                <div className="text-center bg-blue-50 p-3 rounded-lg flex-1 sm:flex-none min-w-[70px]">
                  <p className="text-[10px] text-blue-600 uppercase tracking-wider font-bold">En cours</p>
                  <p className="text-2xl font-black text-blue-600">{activeOrders}</p>
                </div>
                <div className="text-center bg-green-50 p-3 rounded-lg flex-1 sm:flex-none min-w-[70px]">
                  <p className="text-[10px] text-green-600 uppercase tracking-wider font-bold">Livrées</p>
                  <p className="text-2xl font-black text-green-600">{deliveredOrders}</p>
                </div>
                <div className="text-center bg-amber-50 p-3 rounded-lg flex-1 sm:flex-none min-w-[70px]">
                  <p className="text-[10px] text-amber-600 uppercase tracking-wider font-bold">Total</p>
                  <p className="text-lg font-black text-amber-600">{totalAmount.toLocaleString('fr-FR')} <span className="text-[8px]">CFA</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des commandes */}
        {filteredOrders.length > 0 ? (
          <div className="space-y-2">
            {/* Vue Mobile : Cards */}
            <div className="grid grid-cols-1 gap-2 md:hidden">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className={`rounded-lg shadow-sm border overflow-hidden transition ${
                    isOrderDelivered(order)
                      ? 'bg-gray-100 border-gray-200 opacity-70'
                      : 'bg-white border-gray-100'
                  }`}
                >
                  <div className="p-2.5 space-y-2">
                    {/* Ligne 1 : Statut, Téléphone, Lieu, Total */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {/* Badge statut si livrée */}
                        {isOrderDelivered(order) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded flex-shrink-0">
                            <CheckCircleIcon className="h-3 w-3" />
                            Livrée
                          </span>
                        )}

                        {/* Numéro pour appeler */}
                        {order.customer?.phone ? (
                          <a
                            href={`tel:${order.customer.phone}`}
                            className={`flex items-center gap-1 text-xs font-black px-2 py-1 rounded-md flex-shrink-0 ${
                              isOrderDelivered(order) ? 'text-gray-500 bg-gray-200' : 'text-blue-600 bg-blue-50'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PhoneIcon className="h-3 w-3" />
                            {order.customer.phone}
                          </a>
                        ) : (
                          <span className="text-xs font-black text-gray-400 flex-shrink-0">Pas de n°</span>
                        )}

                        {/* Lieu */}
                        <div className="flex items-center gap-1 text-gray-500 min-w-0">
                          <MapPinIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          <p className="text-xs truncate">{order.delivery_address}</p>
                        </div>
                      </div>

                      {/* Montant */}
                      <div className="text-right flex-shrink-0">
                        <span className={`text-sm font-black whitespace-nowrap ${
                          isOrderDelivered(order) ? 'text-gray-500' : 'text-green-600'
                        }`}>
                          {Number(order.total_amount).toLocaleString('fr-FR')} <span className="text-[10px]">CFA</span>
                        </span>
                      </div>
                    </div>

                    {/* Ligne 2 : N° commande, Notes, Boutons */}
                    <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-100">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-[10px] font-bold text-gray-400 flex-shrink-0">#{order.order_number}</span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                        {/* Notes */}
                        <div className="text-[11px] truncate flex-1 italic text-amber-600 font-medium">
                          {order.notes && `Note: ${order.notes}`}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Bouton WhatsApp - ouvre modal si pas de livreur assigné */}
                        {order.customer?.phone && (
                          <button
                            onClick={(e) => handleWhatsAppClick(e, order)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-md bg-green-500 hover:bg-green-600 text-white transition"
                          >
                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.838-6.313-2.234l-.44-.362-3.12 1.046 1.046-3.12-.362-.44A9.955 9.955 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/>
                            </svg>
                            WA
                          </button>
                        )}

                        {/* Bouton Détails */}
                        <button
                          onClick={() => handleViewDetails(order)}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-md transition ${
                            isOrderDelivered(order)
                              ? 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <EyeIcon className="h-3 w-3" />
                          Détails
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Vue Desktop : Tableau */}
            <div className="hidden md:block">
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Commande</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredOrders.map((order) => (
                        <tr
                          key={order.id}
                          className={`transition ${
                            isOrderDelivered(order) ? 'bg-gray-100 opacity-70' : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          {/* N° commande + statut */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                              #{order.order_number}
                            </div>
                            <div className="text-xs mt-1">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                {getStatusLabel(order.status)}
                              </span>
                            </div>
                          </td>
                          {/* Téléphone */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {order.customer?.phone ? (
                              <a
                                href={`tel:${order.customer.phone}`}
                                className={`inline-flex items-center gap-2 text-sm font-bold hover:underline ${
                                  isOrderDelivered(order) ? 'text-gray-500' : 'text-blue-600'
                                }`}
                              >
                                <PhoneIcon className="h-4 w-4" />
                                {order.customer.phone}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Non renseigné</span>
                            )}
                          </td>
                          {/* Adresse */}
                          <td className="px-6 py-4">
                            <div className={`text-sm max-w-xs truncate ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                              <MapPinIcon className="h-4 w-4 inline mr-1 text-gray-400" />
                              {order.delivery_address || '-'}
                            </div>
                          </td>
                          {/* Notes */}
                          <td className="px-6 py-4">
                            <div className="text-sm max-w-xs">
                              {order.notes ? (
                                <span className={`px-2 py-1 rounded text-xs block ${
                                  isOrderDelivered(order) ? 'text-gray-600 bg-gray-200' : 'text-blue-700 bg-blue-50'
                                }`}>
                                  {order.notes}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">-</span>
                              )}
                            </div>
                          </td>
                          {/* Total */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-bold ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                              {Number(order.total_amount).toLocaleString('fr-FR')} {deviseCommande(order)}
                            </div>
                          </td>
                          {/* Actions : WhatsApp + Détails */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {/* Bouton WhatsApp - ouvre modal si pas de livreur assigné */}
                              {order.customer?.phone && (
                                <button
                                  onClick={(e) => handleWhatsAppClick(e, order)}
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-green-500 hover:bg-green-600 text-white transition"
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.838-6.313-2.234l-.44-.362-3.12 1.046 1.046-3.12-.362-.44A9.955 9.955 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/>
                                  </svg>
                                  WhatsApp
                                </button>
                              )}
                              {/* Bouton Détails */}
                              <button
                                onClick={() => handleViewDetails(order)}
                                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
                                  isOrderDelivered(order)
                                    ? 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                }`}
                              >
                                <EyeIcon className="h-4 w-4" />
                                Détails
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Aucune commande */
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <CubeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune commande trouvée
            </h3>
            <p className="text-gray-600">
              {totalOrders > 0
                ? "Toutes les commandes de cette date sont livrées. Activez le toggle pour les voir."
                : "Aucune commande pour cette date."}
            </p>
            {totalOrders > 0 && (
              <button
                onClick={() => setShowDelivered(true)}
                className="mt-4 text-green-600 font-bold hover:underline"
              >
                Afficher les commandes livrées
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de détails de commande */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full sm:max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
            {/* En-tête du modal */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate mr-2">
                Commande #{selectedOrder.order_number}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Statut */}
              <div className="flex justify-center sm:justify-start">
                <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedOrder.status)} shadow-sm`}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Infos client */}
                {selectedOrder.customer && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 shadow-sm">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                      Informations client
                    </h3>
                    <div className="space-y-2">
                      <p className="text-gray-700 text-sm sm:text-base">
                        <span className="font-semibold text-gray-500 text-xs uppercase block sm:inline sm:mr-1">Nom:</span> {selectedOrder.customer.name}
                      </p>
                      {selectedOrder.customer.phone && (
                        <p className="text-gray-700 text-sm sm:text-base">
                          <span className="font-semibold text-gray-500 text-xs uppercase block sm:inline sm:mr-1">Téléphone:</span>{' '}
                          <a href={`tel:${selectedOrder.customer.phone}`} className="text-blue-600 hover:underline font-bold">
                            {selectedOrder.customer.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Détails livraison */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5 text-green-600" />
                    Livraison
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Adresse</p>
                        <p className="text-gray-700 text-sm">{selectedOrder.delivery_address || 'Non renseignée'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Date prévue</p>
                        <p className="text-gray-700 text-sm">
                          {selectedOrder.delivery_date
                            ? new Date(selectedOrder.delivery_date).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                              })
                            : 'Non définie'}
                        </p>
                      </div>
                    </div>

                    {/* Livreur attribué */}
                    <div className="flex items-start gap-2">
                      <UserIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Livreur</p>
                        {selectedOrder.delivery_person ? (
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-gray-700 text-sm">{selectedOrder.delivery_person.name}</span>
                            {selectedOrder.delivery_person.phone && (
                              <a
                                href={`tel:${selectedOrder.delivery_person.phone}`}
                                className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <PhoneIcon className="h-3 w-3" />
                                {selectedOrder.delivery_person.phone}
                              </a>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm italic">Non assigné</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
                  <p className="text-xs font-bold text-amber-900 uppercase mb-1">Notes de la commande</p>
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Articles */}
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CubeIcon className="h-5 w-5 text-green-600" />
                  Articles ({selectedOrder.order_items?.length || 0})
                </h3>
                <div className="space-y-2 border border-gray-100 rounded-lg divide-y divide-gray-100 bg-white">
                  {selectedOrder.order_items?.map((item) => (
                    <div key={item.id} className="flex gap-3 p-3">
                      {/* Image produit - image_url résolu côté serveur selon les options de la variante */}
                      <div
                        className={`w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 rounded-lg bg-gray-50 overflow-hidden border border-gray-100 relative group ${item.image_url ? 'cursor-pointer' : ''}`}
                        onClick={() => item.image_url && setLightboxImage(item.image_url)}
                      >
                        {item.image_url ? (
                          <>
                            <img
                              src={item.image_url}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                              }}
                            />
                            {/* Overlay œil au survol */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all duration-200">
                              <EyeIcon className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CubeIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Infos produit */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <p className="font-medium text-gray-900 text-sm sm:text-base line-clamp-2">
                          {truncateWords(item.product_name, 20)}
                        </p>

                        {/* Options de la variante (ex: Couleur: Noir, Taille: S) */}
                        {item.product_variant?.options && Object.keys(item.product_variant.options).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(item.product_variant.options).map(([key, value]) => (
                              <span
                                key={key}
                                className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded"
                              >
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-bold bg-gray-100 px-1.5 py-0.5 rounded">x{item.quantity}</span>
                          <span className="text-xs text-gray-500">{Number(item.product_price).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}</span>
                        </div>
                        {item.notes && (
                          <p className="text-[10px] sm:text-xs text-amber-600 italic mt-1">Note: {item.notes}</p>
                        )}
                      </div>

                      {/* Prix total article */}
                      <div className="text-right flex flex-col justify-center">
                        <p className="text-sm font-bold text-gray-900">
                          {(Number(item.product_price) * item.quantity).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-green-600 rounded-xl p-4 sm:p-5 text-white shadow-md">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium opacity-90">Total</span>
                  <span className="text-xl sm:text-2xl font-black">
                    {Number(selectedOrder.total_amount).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}
                  </span>
                </div>
                {selectedOrder.shipping_fee > 0 && (
                  <div className="pt-2 border-t border-green-500 border-opacity-30 text-xs flex justify-between opacity-80 italic">
                    <span>Dont frais de livraison:</span>
                    <span>{Number(selectedOrder.shipping_fee).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Pied du modal */}
            <div className="border-t border-gray-200 px-4 sm:px-6 py-4 bg-gray-50 flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                {/* Bouton WhatsApp dans le modal - ouvre modal saisie si pas de livreur */}
                {selectedOrder.customer?.phone && (
                  <button
                    onClick={(e) => handleWhatsAppClick(e, selectedOrder)}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition shadow-md w-full sm:w-auto"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.838-6.313-2.234l-.44-.362-3.12 1.046 1.046-3.12-.362-.44A9.955 9.955 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/>
                    </svg>
                    Envoyer WhatsApp
                  </button>
                )}
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold rounded-xl transition w-full sm:w-auto"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox pour afficher l'image en grand */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-[70]"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-10 right-0 p-1.5 text-white hover:text-gray-300 transition"
            >
              <XMarkIcon className="h-7 w-7" />
            </button>
            <img
              src={lightboxImage}
              alt="Aperçu produit"
              className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Modal de saisie du numéro de livreur (affiché quand pas de livreur assigné) */}
      {showPhoneModal && phoneModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            {/* En-tête du modal */}
            <div className="bg-green-50 border-b border-green-100 px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-green-900 flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-green-600" />
                Numéro du livreur
              </h2>
              <button
                onClick={() => { setShowPhoneModal(false); setPhoneModalOrder(null); }}
                className="p-1.5 hover:bg-green-100 rounded-full transition"
              >
                <XMarkIcon className="h-5 w-5 text-green-700" />
              </button>
            </div>

            {/* Contenu */}
            <div className="p-6">
              {/* Rappel de la commande concernée */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm text-gray-700">
                <CubeIcon className="h-4 w-4" />
                <span>Commande <span className="font-black">#{phoneModalOrder.order_number}</span></span>
              </div>

              <p className="text-sm text-gray-600 mb-3">
                Aucun livreur n'est assigné à cette commande. Saisissez le numéro du livreur pour l'inclure dans le message WhatsApp.
              </p>

              <label htmlFor="manualPhone" className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">
                Numéro de téléphone du livreur
              </label>
              <input
                id="manualPhone"
                type="tel"
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                placeholder="Ex: 0701020304"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-inner text-sm"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handlePhoneModalConfirm(); }}
              />
            </div>

            {/* Pied du modal */}
            <div className="border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row justify-end gap-3 bg-gray-50">
              <button
                onClick={() => { setShowPhoneModal(false); setPhoneModalOrder(null); }}
                className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold rounded-xl transition w-full sm:w-auto order-2 sm:order-1"
              >
                Annuler
              </button>
              <button
                onClick={handlePhoneModalConfirm}
                disabled={!manualPhone.trim()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md w-full sm:w-auto order-1 sm:order-2"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.838-6.313-2.234l-.44-.362-3.12 1.046 1.046-3.12-.362-.44A9.955 9.955 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/>
                </svg>
                Envoyer WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyOrdersPublicView;
